import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService, AuthResponse, TenantInfo } from '../services/api';
import { getAttribution } from '../utils/track';

interface AuthContextType {
  user: AuthResponse['user'] | null;
  tenant: TenantInfo | null;
  isAuthenticated: boolean;
  // True for the current session after a user's first-ever login. Mirrored to
  // sessionStorage so it survives a refresh (until dismissed/logout), then
  // clears with the browser session. (#655)
  isFirstLogin: boolean;
  requires2FA: boolean;
  tempEmail: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, organizationName?: string) => Promise<void>;
  verify2FA: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshTenant: () => Promise<void>;
  applySession: (user: AuthResponse['user'], tenant: TenantInfo | null) => void;
  // Establish a session from an SSO callback JWT (#218): persists the token,
  // fetches the user profile, and stores user/tenant the same way password
  // login does. Throws if the token is rejected.
  // `opts.isFirstLogin` lets the SSO callback pass the first-login flag from
  // the callback fragment, since GET /api/auth/profile does not return it (#688).
  establishSsoSession: (opts?: { isFirstLogin?: boolean }) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Platform roles the API's requireTenantRole lets past every tenant-role check
// (officea11y-api src/middlewares/tenantMiddleware.js). Kept in step with it so
// the UI neither hides from them a control the API would allow, nor offers it
// to anyone the API would refuse.
const TENANT_ROLE_BYPASS_ROLES = new Set(['super_admin', 'admin', 'staff']);

/**
 * Whether the signed-in user may take the tenant owner's actions (OA-196).
 *
 * The API lets only the tenant owner add, remove or change payment methods and
 * open the billing portal, plus the platform staff roles that bypass every
 * tenant-role check, so the UI offers those controls to them alone. Unlike
 * useAuth this does not throw outside an AuthProvider: it answers false there,
 * as it does while no user or tenant is loaded, so a caller hides an owner-only
 * control rather than offering one the API would refuse.
 */
export const useHasOwnerAccess = (): boolean => {
  const context = useContext(AuthContext);
  if (!context?.tenant) {
    return false;
  }
  return (
    context.tenant.tenantRole === 'owner' || TENANT_ROLE_BYPASS_ROLES.has(context.user?.role ?? '')
  );
};

// Session-scoped key so the one-time onboarding dialog survives a page refresh
// (but not a new browser session). See markFirstLogin below. (#655)
const FIRST_LOGIN_KEY = 'officea11y:isFirstLogin';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthResponse['user'] | null>(null);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempEmail, setTempEmail] = useState<string | null>(null);
  // Proof the password step passed, for the 2FA verify call (OA-150). Kept in
  // memory only: it is a short-lived credential, never persisted.
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<string | null>(null);
  // Initialise from sessionStorage so an undismissed first-login dialog
  // survives a dashboard refresh within the same session. (#655)
  const [isFirstLogin, setIsFirstLogin] = useState<boolean>(
    () => sessionStorage.getItem(FIRST_LOGIN_KEY) === 'true'
  );

  // The session cookie is HttpOnly, so the client cannot see whether it
  // exists (#762). A cached profile is the optimistic signal; the server is
  // the authority, and a 401 on any request clears it.
  const isAuthenticated = !!user;

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const storedTenant = localStorage.getItem('tenant');

      // Gated on the cached profile alone. It used to also require a readable
      // token, which no longer exists — leaving that check in place would have
      // meant no session ever restored on reload. getProfile() below is what
      // actually decides: it carries the cookie, and a 401 logs out.
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser) as AuthResponse['user']);
          if (storedTenant) {
            setTenant(JSON.parse(storedTenant) as TenantInfo);
          }
          // Verify token is still valid and refresh tenant data
          const profile: any = await apiService.getProfile();
          const profileUser = profile?.user || profile;
          setUser(profileUser);
          localStorage.setItem('user', JSON.stringify(profileUser));
          if (profile?.tenant) {
            setTenant(profile.tenant);
            localStorage.setItem('tenant', JSON.stringify(profile.tenant));
          } else {
            setTenant(null);
            localStorage.removeItem('tenant');
          }
        } catch {
          // Token is invalid, clear stored data
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
    // Run once on mount; `logout` is used only for invalid-token cleanup and
    // intentionally not a dependency (it would re-run session restoration).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storeAuth = (userData: AuthResponse['user'], tenantData: TenantInfo | null) => {
    setUser(userData);
    setTenant(tenantData);
    // No token is stored: the API set an HttpOnly session cookie on the
    // response that carried this profile (#762).
    localStorage.setItem('user', JSON.stringify(userData));
    if (tenantData) {
      localStorage.setItem('tenant', JSON.stringify(tenantData));
    } else {
      localStorage.removeItem('tenant');
    }
  };

  // Set first-login state and mirror it to sessionStorage so a refresh keeps
  // showing the onboarding dialog until it's dismissed; cleared on logout and
  // on any non-first login. (#655)
  const markFirstLogin = (value: boolean) => {
    setIsFirstLogin(value);
    if (value) {
      sessionStorage.setItem(FIRST_LOGIN_KEY, 'true');
    } else {
      sessionStorage.removeItem(FIRST_LOGIN_KEY);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    const response: any = await apiService.login({ email, password });

    // Check if 2FA is required
    if (response.requires2FA) {
      setRequires2FA(true);
      setTempEmail(email);
      setTwoFactorChallenge(response.challengeToken);
      return true;
    }

    storeAuth(response.user, response.tenant || null);
    // Drives the one-time onboarding dialog; persisted for the session so a
    // refresh before dismissal still shows it.
    markFirstLogin(Boolean(response.user?.isFirstLogin));
    setRequires2FA(false);
    setTempEmail(null);
    setTwoFactorChallenge(null);
    return false;
  };

  const verify2FA = async (token: string) => {
    if (!twoFactorChallenge) {
      throw new Error('Your sign-in has expired. Please sign in again.');
    }

    const response: any = await apiService.verify2FA(twoFactorChallenge, token);

    if (response.user && response.token) {
      storeAuth(response.user, response.tenant || null);
      // Mirror the password-login path so a 2FA user's genuine first login also
      // triggers the onboarding dialog (no-op when the backend omits the flag). (#655)
      markFirstLogin(Boolean(response.user?.isFirstLogin));
      setRequires2FA(false);
      setTempEmail(null);
      setTwoFactorChallenge(null);
    } else {
      throw new Error('2FA verification failed');
    }
  };

  const register = async (email: string, password: string, organizationName?: string) => {
    // The landing URL's campaign tags travel with the sign-up so the API can record them on the
    // account (OA-137). Omitted entirely when there are none.
    const attribution = getAttribution();
    const response = await apiService.register({
      email,
      password,
      organizationName,
      ...(Object.keys(attribution).length > 0 ? { attribution } : {}),
    });
    storeAuth(response.user, response.tenant || null);
  };

  const refreshUser = async () => {
    const profile = await apiService.getProfile();
    const updatedUser = (profile as any).user || profile;
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshTenant = async () => {
    try {
      const result = await apiService.getCurrentTenant();
      setTenant(result.tenant as TenantInfo);
      localStorage.setItem('tenant', JSON.stringify(result.tenant));
    } catch {
      // User may not have a tenant (admin/staff)
      setTenant(null);
      localStorage.removeItem('tenant');
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setTenant(null);
      setRequires2FA(false);
      setTempEmail(null);
      setTwoFactorChallenge(null);
      markFirstLogin(false);
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      apiService.clearStoredSession();
    }
  };

  const applySession = (userData: AuthResponse['user'], tenantData: TenantInfo | null) => {
    storeAuth(userData, tenantData);
  };

  const establishSsoSession = async (opts?: { isFirstLogin?: boolean }) => {
    // Nothing to persist before the profile fetch: the API set the session
    // cookie on the callback redirect itself, and the browser sends it (#762).
    try {
      const profile: any = await apiService.getProfile();
      const profileUser = profile?.user || profile;
      const profileTenant = profile?.tenant || null;
      storeAuth(profileUser, profileTenant);
      // GET /api/auth/profile does not return isFirstLogin (the API only
      // includes it on the login response), so for SSO the flag is passed
      // explicitly from the callback fragment via opts.isFirstLogin (#688).
      // Fall back to the profile field so this still works if the API later
      // adds isFirstLogin to the profile response instead.
      markFirstLogin(opts?.isFirstLogin ?? Boolean(profileUser?.isFirstLogin));
      setRequires2FA(false);
      setTempEmail(null);
      setTwoFactorChallenge(null);
    } catch (error) {
      // Bad/expired session — drop the cached profile so we don't leave a
      // half-authenticated state.
      apiService.clearStoredSession();
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    tenant,
    isAuthenticated,
    isFirstLogin,
    requires2FA,
    tempEmail,
    login,
    register,
    verify2FA,
    logout,
    refreshUser,
    refreshTenant,
    applySession,
    establishSsoSession,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
